/**
 * "More quotes": the market making plans (lib/mmPlan.ts), shown in the builder's slot (pinned
 * right on desktop, a bottom sheet on mobile) when the user is at their tier's limit. Buying
 * opens OpenMarkets' checkout; once paid the builder opens straight away.
 */
import { useState } from "react";
import { fmtCents, TIER_LIMIT, TIER_NAME, type MmOffer } from "../../lib/mmPlan.js";
import { SoftCard } from "../kit.js";
import { btnBase, btnIdle, btnPrimary, type MmPlan } from "./shared.js";

/** Plain words for the checkout outcomes and errors a user can hit. */
function outcomeText(status: string, code?: string): string | null {
  if (status === "canceled" || status === "expired") return null; // backing out isn't an error
  if (status === "pending") return "Waiting for the payment to settle. It unlocks as soon as it does.";
  if (status === "refunded") return "That purchase was refunded.";
  switch (code) {
    case "already_owned":
      return "You already own this. Refreshing your plan…";
    case "product_not_found":
    case "product_not_approved":
      return "This plan isn't available yet.";
    case "billing_disabled":
      return "Purchases aren't enabled for this app yet.";
    case "not_signed_in":
      return "Sign in with OpenMarkets to buy.";
    default:
      return code ? `Checkout couldn't start (${code}).` : "Checkout couldn't start.";
  }
}

export function UnlockQuotes({
  plan,
  active,
  onUnlocked,
  onClose,
}: {
  plan: MmPlan;
  /** Quotes running now. */
  active: number;
  onUnlocked: () => void;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const limit = TIER_LIMIT[plan.tier];

  const buy = async (o: MmOffer) => {
    setBusy(o.code);
    setMsg(null);
    try {
      const r = await plan.purchase(o.code);
      if (r.status === "paid") return onUnlocked();
      if (r.status === "redirected") return; // the page is going to checkout
      setMsg(outcomeText(r.status, r.status === "error" ? r.error.code : undefined));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <h2 className="text-[14px] font-semibold text-ink">More quotes</h2>
        <button type="button" onClick={onClose} aria-label="Close" title="Close" className={`${btnBase} ${btnIdle} ml-auto w-11 !px-0 lg:w-8`}>
          ✕
        </button>
      </div>
      <p className="text-[13px] text-dim">
        {TIER_NAME[plan.tier]} includes {limit} active quote{limit === 1 ? "" : "s"}. You have <span className="nums text-ink">{active}</span> running.
      </p>

      {plan.offers.length === 0 ? (
        <SoftCard className="p-3 text-[13px] text-dim">{plan.error ?? "No upgrades available right now."}</SoftCard>
      ) : (
        plan.offers.map((o) => (
          <SoftCard key={o.code} className="flex items-center gap-3 p-3">
            <div className="min-w-0 flex-1">
              <div className="text-[14px] font-semibold text-ink">{TIER_NAME[o.tier]}</div>
              <div className="text-[12px] text-dim">{o.blurb}</div>
              {o.note && <div className="text-[11px] text-faint">{o.note}</div>}
            </div>
            <button
              type="button"
              disabled={busy !== null}
              onClick={() => void buy(o)}
              className={`${btnBase} ${btnPrimary} nums min-w-[5.5rem] font-semibold`}
            >
              {busy === o.code ? "Opening…" : fmtCents(o.priceCents)}
            </button>
          </SoftCard>
        ))
      )}

      {msg && <p className="text-[12px] text-amber">{msg}</p>}
      <p className="text-[11px] text-faint">One-time purchase. You confirm and pay on OpenMarkets. Closed quotes don't count toward the limit.</p>
    </div>
  );
}
